import React, { useState, useEffect } from 'react'
import Modal from "react-bootstrap/Modal";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import mammoth from "mammoth/mammoth.browser";
import "../../assets/scss/tables.scss";

/* The server sends everything through res.download(), which labels files as
   attachments. A blob needs the real type or the browser will not render it. */
const MIME = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    txt: "text/plain",
};

const readableSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ResumeViewModal = ({ resumeData, show, onHide }) => {

    /* idle | loading | ready | missing | error */
    const [state, setState] = useState("idle");
    const [percent, setPercent] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [blobUrl, setBlobUrl] = useState("");
    const [docHtml, setDocHtml] = useState("");

    const fileType = String(resumeData.filetype || "").toLowerCase();
    const isWord = fileType === "docx" || fileType === "doc";
    const isImage = ["png", "jpg", "jpeg", "gif", "webp"].includes(fileType);

    useEffect(() => {
        if (!show || !resumeData.filePath) return undefined;

        let objectUrl = "";
        let cancelled = false;
        const xhr = new XMLHttpRequest();

        setState("loading");
        setPercent(0);
        setLoaded(0);
        setDocHtml("");

        xhr.open("GET", resumeData.filePath, true);
        /* an ArrayBuffer serves both paths: Word needs the bytes, everything
           else gets wrapped in a Blob with the correct type */
        xhr.responseType = "arraybuffer";

        xhr.onprogress = (event) => {
            setLoaded(event.loaded);
            if (event.lengthComputable && event.total) {
                setPercent(Math.round((event.loaded / event.total) * 100));
            }
        };

        xhr.onload = async () => {
            if (cancelled) return;

            if (xhr.status === 404) { setState("missing"); return; }
            if (xhr.status < 200 || xhr.status >= 300 || !xhr.response || !xhr.response.byteLength) {
                setState("error");
                return;
            }

            setPercent(100);

            if (isWord) {
                try {
                    const result = await mammoth.convertToHtml({ arrayBuffer: xhr.response });
                    if (cancelled) return;
                    setDocHtml(result.value || "<p>This document appears to be empty.</p>");
                    setState("ready");
                } catch (err) {
                    if (!cancelled) setState("error");
                }
                return;
            }

            const type = MIME[fileType] || "application/octet-stream";
            objectUrl = URL.createObjectURL(new Blob([xhr.response], { type }));
            setBlobUrl(objectUrl);
            setState(MIME[fileType] ? "ready" : "error");
        };

        xhr.onerror = () => { if (!cancelled) setState("error"); };
        xhr.send();

        return () => {
            cancelled = true;
            xhr.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setBlobUrl("");
            setDocHtml("");
            setState("idle");
        };
    }, [show, resumeData.filePath, fileType, isWord]);

    const title = resumeData.resumeName ? `${resumeData.resumeName} — Resume` : "Resume";

    return (
        <Modal size="lg" show={show} onHide={onHide} className="wh-modal wh-modal-tall"
            aria-labelledby="resume-modal-title">
            <Modal.Header closeButton>
                <Modal.Title id="resume-modal-title">{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="wh-viewer">

                {state === "loading" && (
                    <div className="wh-viewer-state">
                        <div className="wh-progress">
                            <CircularProgress
                                variant={percent ? "determinate" : "indeterminate"}
                                value={percent} size={78} thickness={3.5} />
                            <span>{percent ? `${percent}%` : ""}</span>
                        </div>
                        <h6>Loading the resume</h6>
                        <p>{readableSize(loaded) || "Starting the download"}</p>
                    </div>
                )}

                {state === "missing" && (
                    <div className="wh-viewer-state">
                        <ErrorOutlineRoundedIcon className="wh-viewer-error" />
                        <h6>This resume is no longer on the server</h6>
                        <p>
                            The record still points at <b>{resumeData.filePath?.split("/").pop()}</b>,
                            but that file is not in the uploads folder. Re-upload it from Edit candidate.
                        </p>
                    </div>
                )}

                {state === "error" && (
                    <div className="wh-viewer-state">
                        <ErrorOutlineRoundedIcon className="wh-viewer-error" />
                        <h6>This resume could not be shown</h6>
                        <p>{fileType
                            ? `.${fileType} files cannot be previewed in the browser.`
                            : "The file type could not be determined."} You can still download it.</p>
                        <Button variant="contained" startIcon={<DownloadRoundedIcon />}
                            href={resumeData.filePath} target="_blank" rel="noopener noreferrer">
                            Download instead
                        </Button>
                    </div>
                )}

                {state === "ready" && isWord && (
                    <div className="wh-viewer-frame wh-viewer-doc">
                        <div className="wh-paper wh-rich-text"
                            dangerouslySetInnerHTML={{ __html: docHtml }} />
                    </div>
                )}

                {state === "ready" && isImage && (
                    <div className="wh-viewer-frame wh-viewer-doc">
                        <img className="wh-paper" src={blobUrl} alt={title} />
                    </div>
                )}

                {state === "ready" && !isWord && !isImage && (
                    <iframe className="wh-viewer-frame" src={blobUrl} title={title} />
                )}

            </Modal.Body>

            <Modal.Footer>
                <div className="wh-viewer-name">
                    <DescriptionOutlinedIcon />
                    <span>{fileType ? fileType.toUpperCase() : "FILE"}</span>
                </div>
                <Button variant="contained" color="inherit" onClick={onHide}>Close</Button>
                <Button variant="contained" startIcon={<DownloadRoundedIcon />}
                    href={resumeData.filePath} target="_blank" rel="noopener noreferrer"
                    disabled={!resumeData.filePath || state === "missing"}>
                    Download
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ResumeViewModal;
